import config from '@/common/config';
import timezone from '@/common/timezone';
import {DateTimeFormats} from '@/common/datetime-formats';

import shortid from 'shortid';

const {
    VUE_APP_API_URL,
    VUE_APP_API_VERSION,
} = config;

export default class EntityModel {
    constructor(fields) {
        // used for entity tracking within UI collections
        this.$internalId = shortid.generate();

        // parse entity links
        this.$links = EntityModel.hasLinks({fields})
            ? this.constructor.parseLinks({
                fields,
                links: fields._links,
                prefix: `${VUE_APP_API_URL}/${VUE_APP_API_VERSION}/`,
            })
            : null;
        this.$specialProps = this.constructor.parseSpecialProps({fields, links: this.$links});
        this.hydrate(fields);
    }

    static ToggleStatuses = {
        active: {
            name: 'active',
            value: true,
        },
        inactive: {
            name: 'inactive',
            value: false,
        },
    };

    /**
     * Create a clone of this model and its methods
     * and properties. Discards static attributes
     * on the prototype. This is particularly useful
     * for component parent-to-child communication and
     * API payload generation in complex models.
     * Caveat: collections of models within the parent model will lose
     * their $internalId uniqueness. Alternatively the model can define
     * its own use-case of the cloneSelf method.
     * @param fields {Object} define fields to override the current
     * properties
     * @param dirty {boolean} whether to copy the existing properties
     * or start from a blank state. Note that clean copies will not
     * keep hydrated properties from inherited classes.
     * @returns {EntityModel}
     */
    cloneSelf(fields = {}, dirty = true) {
        const copiedData = dirty
            ? {...this.getPayload(), ...fields}
            // note: clean copies will miss inherited
            // properties from ancestor hydration
            : fields;
        const clone = new this.constructor(copiedData);
        // keep the same internal ID on the clone
        clone.$internalId = this.$internalId;
        return clone;
    }

    /* eslint-disable no-underscore-dangle */
    static hasEmbedded({fields, entityName}) {
        return fields._embedded && entityName in fields._embedded;
    }

    /* eslint-disable no-underscore-dangle, new-cap */
    static fromEmbedded({fields, entityName, model}) {
        let modelData = false;
        if (EntityModel.hasEmbedded({fields, entityName})) {
            modelData = fields._embedded[entityName];
        }
        return modelData ? new model(modelData) : null;
    }

    static hasLinks({fields}) {
        return !!(fields && fields._links && fields._links.length);
    }

    static convertStringStatus(state) {
        return typeof state === 'string' && state === EntityModel.ToggleStatuses.active.name;
    }

    static convertBooleanStatus(state) {
        return state
            ? EntityModel.ToggleStatuses.active.name
            : EntityModel.ToggleStatuses.inactive.name;
    }

    static datetimeToMoment(datetime) {
        return timezone.readUTC(datetime);
    }

    static timeToMoment(time) {
        return timezone.readTime(time);
    }

    static get timeAPIFormat() {
        return DateTimeFormats.timeApiFormat;
    }

    static parseSpecialProps({links}) {
        const props = {};
        if (links && links.self) {
            props.entityId = links.self.entityId;
            props.entityName = links.self.entityName;
        }
        return props;
    }

    static parseLinks({links, prefix}) {
        return links.reduce((memo, link) => {
            let [
                entityName = null,
                // eslint-disable-next-line prefer-const
                entityId = null,
            ] = link.href.replace(prefix, '').split('/');
            if (entityName && entityName.includes('?')) {
                entityName = entityName.split('?').shift();
            }
            return {
                ...memo,
                [link.rel]: {
                    entityName,
                    entityId,
                    href: link.href,
                },
            };
        }, {});
    }

    /* eslint-disable class-methods-use-this */
    /**
     * Hydrate the model with the field data received from the API.
     */
    hydrate() {
        throw new Error('Method must be implemented in extended model.');
    }

    /**
     * Convert the model fields into a payload that can be sent to the API.
     *
     * The method should only be used to generate data for API requests.
     */

    /* eslint-disable class-methods-use-this */
    getPayload() {
        throw new Error('Method must be implemented in extended model.');
    }

    /**
     * @param payload {object} The payload to remove null values from.
     * @returns {object} The payload object without key's with null values.
     */
    removeNullPayloadValues(payload) {
        return Object.keys(payload)
            .filter(key => payload[key] !== null && payload[key] !== undefined)
            .reduce((newObj, key) => (typeof payload[key] === 'object'
                ? Object.assign(newObj, {[key]: this.removeNullPayloadValues(payload[key])})
                : Object.assign(newObj, {[key]: payload[key]})),
            {});
    }
}
